import { api } from './api';
import type {
  NoteWithRelations,
  CreateNoteRequest,
  UpdateNoteRequest,
  NoteFilters,
  NotesResponse,
  NoteResponse,
  NoteSearchResponse,
  ConvertToTaskResponse,
} from '../types/note';

const NOTES_URL = '/notes';

/**
 * Get all notes with filters and pagination
 */
export const getNotes = async (filters?: NoteFilters): Promise<NotesResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.type?.length) {
      params.append('type', filters.type.join(','));
    }
    if (filters.clientId) {
      params.append('clientId', filters.clientId);
    }
    if (filters.pinned) {
      params.append('pinned', 'true');
    }
    if (filters.archived !== undefined) {
      params.append('archived', String(filters.archived));
    }
    if (filters.tags?.length) {
      params.append('tags', filters.tags.join(','));
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString ? `${NOTES_URL}?${queryString}` : NOTES_URL;

  const response = await api.get<NotesResponse>(url);
  return response.data;
};

/**
 * Search notes
 */
export const searchNotes = async (query: string): Promise<NoteSearchResponse> => {
  const response = await api.get<NoteSearchResponse>(`${NOTES_URL}/search`, {
    params: { q: query },
  });
  return response.data;
};

/**
 * Get a single note by ID
 */
export const getNote = async (id: string): Promise<NoteResponse> => {
  const response = await api.get<NoteResponse>(`${NOTES_URL}/${id}`);
  return response.data;
};

/**
 * Create a new note
 */
export const createNote = async (data: CreateNoteRequest): Promise<NoteResponse> => {
  const response = await api.post<NoteResponse>(NOTES_URL, data);
  return response.data;
};

/**
 * Update a note
 */
export const updateNote = async (
  id: string,
  data: UpdateNoteRequest
): Promise<NoteResponse> => {
  const response = await api.patch<NoteResponse>(`${NOTES_URL}/${id}`, data);
  return response.data;
};

/**
 * Delete a note
 */
export const deleteNote = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`${NOTES_URL}/${id}`);
  return response.data;
};

/**
 * Toggle pin status
 */
export const togglePinNote = async (id: string): Promise<NoteResponse> => {
  const response = await api.post<NoteResponse>(`${NOTES_URL}/${id}/pin`);
  return response.data;
};

/**
 * Toggle archive status
 */
export const toggleArchiveNote = async (id: string): Promise<NoteResponse> => {
  const response = await api.post<NoteResponse>(`${NOTES_URL}/${id}/archive`);
  return response.data;
};

/**
 * Convert note to task
 */
export const convertNoteToTask = async (id: string): Promise<ConvertToTaskResponse> => {
  const response = await api.post<ConvertToTaskResponse>(`${NOTES_URL}/${id}/convert-to-task`);
  return response.data;
};

/**
 * Get notes for a specific client
 */
export const getClientNotes = async (clientId: string): Promise<{ success: boolean; data: { notes: NoteWithRelations[] } }> => {
  const response = await api.get(`/clients/${clientId}/client-notes`);
  return response.data;
};

export default {
  getNotes,
  searchNotes,
  getNote,
  createNote,
  updateNote,
  deleteNote,
  togglePinNote,
  toggleArchiveNote,
  convertNoteToTask,
  getClientNotes,
};
